import {
  type MyEstimateGroup,
  type MyEstimateResponse,
  useDeclineMyEstimateResponseMutation,
  useDownloadMyEstimatePdfMutation,
  useGetMyEstimatesQuery,
  useRequestMyEstimateModificationMutation,
  useRequestMyEstimateSimilarMutation,
} from '@massilia/queries'
import { Button, downloadBlob } from '@massilia/ui'
import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronDown, Loader2, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { EstimateResponseActions } from './estimate-response-card'

import { AcceptEstimateDialog } from './accept-estimate-dialog'
import { DeclineEstimateDialog } from './decline-estimate-dialog'
import { EstimateRequestDrawer } from './estimate-request-drawer'
import { EstimateRequestRow } from './estimate-request-row'
import { RequestModificationDialog } from './request-modification-dialog'
import { similarRequestFor } from './similar-request'

/** Demandes encore vivantes, par opposition à l'historique replié. */
const isOngoing = (group: MyEstimateGroup) => group.status !== 'CLOSED'

type OpenDialog = 'accept' | 'decline' | 'modification'

export function EstimatesPage() {
  const navigate = useNavigate()
  const { data: groups = [], isLoading, refetch } = useGetMyEstimatesQuery()

  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [openedGroupId, setOpenedGroupId] = useState<null | string>(null)
  const [dialog, setDialog] = useState<null | { kind: OpenDialog; responseId: string }>(null)

  const decline = useDeclineMyEstimateResponseMutation()
  const requestModification = useRequestMyEstimateModificationMutation()
  const requestSimilar = useRequestMyEstimateSimilarMutation()
  const downloadPdf = useDownloadMyEstimatePdfMutation()

  /**
   * Un 409 ne dit pas « ça a raté » mais « l'état a changé depuis que vous avez
   * ouvert cet écran » — typiquement un second onglet, ou l'agence qui a repris
   * la demande entre-temps. Annoncer un échec y était faux : la proposition
   * ÉTAIT refusée, et le client rejouait le geste en boucle sur un panneau qui
   * ne se rafraîchissait pas.
   */
  const handleActionError = (cause: unknown, failureMessage: string) => {
    const status = (cause as undefined | { response?: { status?: number } })?.response?.status

    if (status === 409) {
      toast.info('Cette proposition a changé entre-temps', {
        description: 'Nous avons rechargé vos devis pour vous montrer son état actuel.',
      })
      setDialog(null)
    } else {
      toast.error(failureMessage)
    }

    // On recharge les devis dans les deux cas : sur un 409 c'est le fond du
    // sujet, et sur une panne c'est ce qui remet l'écran d'accord avec le
    // serveur plutôt que de laisser un état qu'on ne sait plus expliquer.
    void refetch()
  }

  const ongoing = groups.filter(isOngoing)
  const history = groups.filter((group) => !isOngoing(group))

  // Relu depuis la liste plutôt que gardé en état : après un refus ou une
  // demande de modification, le panneau doit montrer la version rafraîchie.
  const openedGroup = groups.find((group) => group.id === openedGroupId) ?? null

  useHashOpensRequest(groups, setOpenedGroupId)

  const findResponse = (responseId: string): MyEstimateResponse | undefined =>
    groups.flatMap((group) => group.responses).find((response) => response.id === responseId)

  /**
   * Autres propositions de la MÊME demande encore vivantes : ce sont elles que
   * la confirmation déclinera, et c'est ce nombre qu'annonce le dialog.
   */
  const countOtherLiveResponses = (responseId: string) => {
    const group = groups.find((candidate) =>
      candidate.responses.some((response) => response.id === responseId)
    )

    return (group?.responses ?? []).filter(
      (response) => response.id !== responseId && response.canAct
    ).length
  }

  const handleDownload = (responseId: string) => {
    downloadPdf.mutate(responseId, {
      onError: () => toast.error('Impossible de télécharger le devis'),
      // Nommé d'après la proposition : télécharger les trois d'une demande
      // donnait « devis.pdf », « devis (1).pdf », « devis (2).pdf », et le
      // client ne savait plus laquelle il relisait.
      onSuccess: (blob) => downloadBlob(blob, quotePdfName(findResponse(responseId)?.name)),
    })
  }

  const buildActions = (responseId: string): EstimateResponseActions | null => ({
    onAccept: () => setDialog({ responseId, kind: 'accept' }),
    onDecline: () => setDialog({ responseId, kind: 'decline' }),
    onDownloadPdf: () => handleDownload(responseId),
    onRequestModification: () => setDialog({ responseId, kind: 'modification' }),
  })

  const activeResponse = dialog ? findResponse(dialog.responseId) : undefined
  const downloadingResponseId = downloadPdf.isPending ? (downloadPdf.variables ?? null) : null

  if (isLoading) {
    return (
      <main className="flex flex-1 items-center justify-center bg-white py-14">
        <Loader2 className="size-8 animate-spin text-brand-600" />
      </main>
    )
  }

  return (
    <main className="flex flex-1 flex-col gap-8 bg-white px-4 py-8 md:px-28 md:py-10">
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <h2 className="text-xl font-semibold text-gray-light-900">En cours</h2>
        <Button asChild className="gap-1 bg-brand-600 text-white hover:bg-brand-700">
          <Link to="/mes-devis/nouvelle-demande">
            <Plus className="size-5" />
            Nouvelle demande
          </Link>
        </Button>
      </div>

      {ongoing.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {ongoing.map((group) => (
            <li key={group.id}>
              <EstimateRequestRow group={group} onOpen={() => setOpenedGroupId(group.id)} />
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col items-center rounded-3xl border border-gray-light-200 px-6 py-14 md:px-8">
          <img
            alt="Avion"
            className="mb-6 h-38 w-61 max-w-full rounded-4xl object-cover"
            src="/images/empty-state-airplane.jpg"
          />
          <h3 className="mb-6 text-center text-base font-semibold text-gray-light-900">
            Vous n&apos;avez aucune demande en cours
          </h3>
          <Button asChild className="gap-1 bg-brand-600 text-white hover:bg-brand-700">
            <Link to="/mes-devis/nouvelle-demande">
              <Plus className="size-5" />
              Demander un devis
            </Link>
          </Button>
        </div>
      )}

      {history.length > 0 && (
        <section className="flex flex-col gap-4">
          <button
            className="flex w-fit items-center gap-2 text-xl font-semibold text-gray-light-900"
            onClick={() => setIsHistoryOpen((open) => !open)}
            type="button"
          >
            Historique ({history.length})
            <ChevronDown
              className={`size-5 transition-transform ${isHistoryOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isHistoryOpen && (
            <ul className="flex flex-col gap-3">
              {history.map((group) => (
                <li key={group.id}>
                  <EstimateRequestRow group={group} onOpen={() => setOpenedGroupId(group.id)} />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <EstimateRequestDrawer
        buildActions={buildActions}
        buildRequestSimilar={(responseId) => similarRequestFor(requestSimilar, responseId)}
        downloadingResponseId={downloadingResponseId}
        group={openedGroup}
        onOpenChange={(open) => !open && setOpenedGroupId(null)}
      />

      {activeResponse && dialog?.kind === 'accept' && (
        <AcceptEstimateDialog
          isOpen
          onConfirm={() => {
            void navigate({
              params: { estimateResponseId: activeResponse.id },
              to: '/mes-devis/$estimateResponseId/reserver',
            })
          }}
          onOpenChange={(open) => !open && setDialog(null)}
          otherLiveResponsesCount={countOtherLiveResponses(activeResponse.id)}
          responseName={activeResponse.name}
        />
      )}

      {activeResponse && dialog?.kind === 'decline' && (
        <DeclineEstimateDialog
          isOpen
          isSubmitting={decline.isPending}
          onConfirm={(payload) =>
            decline.mutate(
              { ...payload, responseId: activeResponse.id },
              {
                onError: (error) =>
                  handleActionError(error, 'Impossible d’enregistrer votre refus'),
                onSuccess: () => {
                  toast.success('Votre refus a bien été transmis')
                  setDialog(null)
                },
              }
            )
          }
          onOpenChange={(open) => !open && setDialog(null)}
          responseName={activeResponse.name}
        />
      )}

      {activeResponse && dialog?.kind === 'modification' && (
        <RequestModificationDialog
          isOpen
          isSubmitting={requestModification.isPending}
          onConfirm={(payload) =>
            requestModification.mutate(
              { ...payload, responseId: activeResponse.id },
              {
                onError: (error) => handleActionError(error, 'Impossible d’envoyer votre demande'),
                onSuccess: () => {
                  toast.success('Votre demande a bien été transmise')
                  setDialog(null)
                },
              }
            )
          }
          onOpenChange={(open) => !open && setDialog(null)}
          responseName={activeResponse.name}
        />
      )}
    </main>
  )
}

/**
 * Nom de fichier d'un devis téléchargé, dérivé du nom de la proposition.
 * Retombe sur `devis.pdf` si la proposition est introuvable — un téléchargement
 * ne doit jamais échouer sur un nom.
 */
function quotePdfName(responseName: string | undefined): string {
  const slug = (responseName ?? '')
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036F]/g, '')
    .replaceAll(/[^a-zA-Z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '')
    .toLowerCase()

  return slug ? `devis-${slug}.pdf` : 'devis.pdf'
}

/**
 * Ouvre la demande désignée par le `#…` de l'URL.
 *
 * Deux entrées y mènent : les mails tarifaires 55/56, qui pointent
 * `/mes-devis#<idDemande>`, et la page publique du devis, qui y renvoie un
 * client connecté avec `#<idProposition>`. La liste ne montrant qu'un résumé,
 * s'y arrêter laisserait le visiteur devant une ligne repliée alors qu'on lui a
 * promis sa proposition — on ouvre donc le panneau pour lui.
 *
 * Une seule fois : refermer le panneau ne doit pas le voir se rouvrir au rendu
 * suivant, et le `#` reste dans l'URL.
 */
function useHashOpensRequest(
  groups: MyEstimateGroup[],
  openGroup: (groupId: string) => void
): void {
  const handledHash = useRef<null | string>(null)

  useEffect(() => {
    const applyHash = () => {
      const hash = globalThis.location.hash.slice(1)
      if (!hash || handledHash.current === hash || groups.length === 0) return

      const target = groups.find(
        (group) => group.id === hash || group.responses.some((response) => response.id === hash)
      )
      if (!target) return

      handledHash.current = hash
      openGroup(target.id)
      document.querySelector(`#${CSS.escape(target.id)}`)?.scrollIntoView({ block: 'center' })
    }

    applyHash()

    // `hashchange` : sans lui, le lien du mail ne marchait qu'au CHARGEMENT de
    // la page. Recliqué depuis un onglet déjà ouvert sur `/mes-devis`, il ne
    // faisait rien du tout — l'URL changeait, la page restait.
    const onHashChange = () => {
      handledHash.current = null
      applyHash()
    }

    globalThis.addEventListener('hashchange', onHashChange)
    return () => globalThis.removeEventListener('hashchange', onHashChange)
  }, [groups, openGroup])
}
